import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, ShoppingBagIcon, MessageSquareIcon, MapPinIcon, TruckIcon, WrenchIcon, SprayCanIcon, RulerIcon } from 'lucide-react';
import { Furniture } from '../../data/furniture';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';

/**
 * The measurements, in full. A furniture detail view that hides dimensions
 * behind prose is useless — people open it holding a tape measure.
 */
export function FurnitureDetailModal({
  id,
  title,
  location,
  furniture,
  initialFinishId,
  onClose
}: {
  id: string;
  title: string;
  location: string;
  furniture: Furniture;
  initialFinishId?: string;
  onClose: () => void;
}) {
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen
  } = useCart();
  const navigate = useNavigate();
  const [finishId, setFinishId] = useState(initialFinishId ?? furniture.finishes[0].id);
  const finish = furniture.finishes.find(f => f.id === finishId) ?? furniture.finishes[0];
  const index = furniture.finishes.findIndex(f => f.id === finish.id);
  const store = location.split(' · ')[0];
  const {
    w,
    d,
    h
  } = furniture.dimensions;
  const canOrder = furniture.ordering !== false;
  const lead = finish.leadTime ?? furniture.leadTime;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  const step = (dir: 1 | -1) => setFinishId(furniture.finishes[(index + dir + furniture.finishes.length) % furniture.finishes.length].id);
  const measurements = [{
    label: 'Width',
    value: `${w} cm`
  }, {
    label: 'Depth',
    value: `${d} cm`
  }, {
    label: 'Height',
    value: `${h} cm`
  }];
  const logistics = [{
    icon: TruckIcon,
    text: lead
  }, {
    icon: WrenchIcon,
    text: furniture.assembly
  }, {
    icon: TruckIcon,
    text: furniture.delivery
  }, ...(furniture.care ? [{
    icon: SprayCanIcon,
    text: furniture.care
  }] : [])];
  return createPortal(<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-2237" data-mp="1yxu10oz3zm1s">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2238" data-mp="2321raql7ta24" />
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-modal="true" aria-label={title} className="relative w-full sm:max-w-4xl bg-white rounded-t-3xl sm:rounded-3xl shadow-large overflow-hidden flex flex-col sm:flex-row max-h-[92vh] sm:max-h-[88vh] sm:h-[620px]" data-id="element-2239" data-mp="280d51cems4pm">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-slate-600 hover:text-dark-navy shadow-sm transition-colors duration-150 ease-out" data-id="element-2240" data-mp="b14cracpf27q">
          <XIcon className="w-4 h-4" data-id="element-2241" data-mp="yr71bghetq1l" />
        </button>

        <div className="relative flex-shrink-0 h-52 sm:h-auto sm:w-[52%] bg-slate-100 group" data-id="element-2242" data-mp="ysry7cr75l2m">
          <img src={finish.image} alt={`${title} in ${finish.label}`} className="w-full h-full object-cover" data-id="element-2243" data-mp="ty5n7ap11uj2" />
          {furniture.finishes.length > 1 && <>
              <button onClick={() => step(-1)} aria-label="Previous finish" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-slate-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-2244" data-mp="4kayhstibx3a">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-2245" data-mp="119ikf1y1g87q" />
              </button>
              <button onClick={() => step(1)} aria-label="Next finish" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-slate-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-2246" data-mp="ni41sw81m917">
                <ChevronRightIcon className="w-5 h-5" data-id="element-2247" data-mp="1ucuuh6ndm3pd" />
              </button>
              <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-2248" data-mp="1uaqg1urupmau">
                {furniture.finishes.map((f, i) => <span key={f.id} className={`h-1.5 rounded-full transition-all duration-200 ease-out ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`} data-id="element-2249" data-mp="2435z6vevwgxb" data-mpi={i} />)}
              </div>
            </>}
        </div>

        <div className="flex-1 flex flex-col min-w-0" data-id="element-2250" data-mp="17df4ow6letri">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 pr-10 space-y-5" data-id="element-2251" data-mp="17ezz8lswqcpd">
            <div data-id="element-2252" data-mp="1ldpxi4rgqifb">
              <MerchantBadge merchant={merchantFor(furniture.brand, 'Furniture')} showKind data-id="element-2253" data-mp="1la23z5m0pw0l" />
              <h2 className="font-display font-bold text-xl text-dark-navy mt-3 leading-snug" data-id="element-2254" data-mp="2wqscivdwyzxd">
                {title}
              </h2>
              <p className="text-[13px] font-semibold text-slate-500 mt-1" data-id="element-2255" data-mp="2rs8rqyb23u6l">
                {furniture.type} · {furniture.material}
              </p>
              <div className="flex items-baseline gap-2 mt-2.5" data-id="element-2256" data-mp="33u8kj6m7hlaw">
                <span className="font-display font-bold text-2xl text-dark-navy" data-id="element-2257" data-mp="33wmhxm4cz9lm">
                  {money(furniture.price)}
                </span>
                {furniture.wasPrice && <>
                    <span className="text-[13px] text-slate-400 line-through" data-id="element-2258" data-mp="2g57714yinjyr">
                      {money(furniture.wasPrice)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-dark-navy text-white text-[11px] font-bold" data-id="element-2259" data-mp="2l4tknfi9b283">
                      {Math.round((1 - furniture.price / furniture.wasPrice) * 100)}
                      % off
                    </span>
                  </>}
              </div>
              <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 mt-2" data-id="element-2260" data-mp="22d96hiivewe7">
                <MapPinIcon className="w-3.5 h-3.5" data-id="element-2261" data-mp="1uu0100dhjhuf" />
                {location}
              </p>
            </div>

            {/* Measure twice */}
            <div data-id="element-2262" data-mp="1pvyf77ood4f9">
              <h3 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2263" data-mp="1ptc8fa9lysy3">
                <RulerIcon className="w-3.5 h-3.5" data-id="element-2264" data-mp="1fx94pzz3ym91" />
                Measurements
              </h3>
              <div className="grid grid-cols-3 gap-2 mt-2.5" data-id="element-2265" data-mp="1bv5lgp676dd7">
                {measurements.map(m => <div key={m.label} className="bg-slate-50 rounded-xl px-3 py-2.5" data-id="element-2266" data-mp="1745oxl0zpxb8">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2267" data-mp="125voauwhy4i8">
                      {m.label}
                    </span>
                    <span className="block text-[15px] font-bold text-dark-navy mt-1" data-id="element-2268" data-mp="s6d7gnbvuyue">
                      {m.value}
                    </span>
                  </div>)}
              </div>
            </div>

            <div data-id="element-2269" data-mp="2c3280isl4eye">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2270" data-mp="2c0fzogxvdvf4">
                Finish — {finish.label}
              </h3>
              <div className="flex items-center gap-2.5 mt-2.5" data-id="element-2271" data-mp="2254og8lo76c4">
                {furniture.finishes.map(f => <button key={f.id} onClick={() => setFinishId(f.id)} aria-label={f.label} aria-pressed={f.id === finishId} className={`w-9 h-9 rounded-full border transition-[box-shadow,border-color] duration-150 ease-out ${f.id === finishId ? 'border-dark-navy ring-2 ring-dark-navy/15' : 'border-slate-200 hover:border-slate-400'}`} style={{
                backgroundColor: f.hex
              }} data-id="element-2272" data-mp="2724q87bhlmxf" />)}
              </div>
              {finish.leadTime && <p className="text-[12px] text-slate-500 mt-2.5" data-id="element-2273" data-mp="1egv83m787asd">
                  This finish is {finish.leadTime.toLowerCase()}.
                </p>}
            </div>

            <div className="space-y-2" data-id="element-2274" data-mp="qt7zinnohzwj">
              {logistics.map(l => <p key={l.text} className="flex items-start gap-2.5 text-[13px] text-slate-600 leading-snug" data-id="element-2275" data-mp="14rxxe5ibo3ly">
                  <l.icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-2276" data-mp="1eosv4m6rtzz6" />
                  {l.text}
                </p>)}
            </div>

            {!canOrder && <p className="px-4 py-3 bg-slate-50 rounded-2xl text-[12px] text-slate-500 leading-relaxed" data-id="element-2277" data-mp="1nhebjvmxjuku">
                {furniture.brand} builds to order and quotes directly. Message
                them to confirm dimensions, finish and lead time.
              </p>}
          </div>

          <div className="px-5 sm:px-6 py-4 border-t border-slate-100 flex-shrink-0" data-id="element-2278" data-mp="1sgzcgblnud07">
            {canOrder ? <button onClick={() => requireAuth(() => {
            add({
              listingId: id,
              merchant: store,
              title,
              image: finish.image,
              summary: `${finish.label} · ${w} × ${d} × ${h} cm`,
              unitPrice: furniture.price,
              qty: 1
            });
            onClose();
            setOpen(true);
          })} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2279" data-mp="1fl955iqlmj0c">
                <ShoppingBagIcon className="w-4 h-4" data-id="element-2280" data-mp="1kmgh7tzry4ct" />
                Add {finish.label} · {money(furniture.price)}
              </button> : <button onClick={() => requireAuth(() => navigate(`/messages?to=${encodeURIComponent(furniture.brand)}&about=${encodeURIComponent(title)}`))} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2281" data-mp="945mb1vpwnt9">
                <MessageSquareIcon className="w-4 h-4" data-id="element-2282" data-mp="hqyj2qnb2bl9" />
                Message {furniture.brand}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>, document.body);
}